/**
 * Team Member Modal
 * --------------------------------------------------
 * Handles team member modal functionality
 * Clones modal template from DOM, populates it, adds to body, and destroys on close
 * Uses PageUtils for body scroll locking
 *
 * Usage:
 * - Add data-view-team-member to team member cards
 * - Store team member data in data attributes on the card button
 * - Template must exist in DOM with id="team-member-modal-template"
 */

import { PageUtils } from "./helpers/page-utils";

window.addEventListener('load', () => {
    const template = document.getElementById('team-member-modal-template');

    if (!template) return;

    let modal = null;
    let modalBackdrop = null;
    let modalContent = null;

    /**
     * Clone template and add to DOM
     */
    function createModalFromTemplate() {
        // Clone the template content
        const clone = template.content.cloneNode(true);

        // Append to body
        document.body.appendChild(clone);

        // Get references to the newly added elements
        modal = document.getElementById('team-member-modal');
        modalBackdrop = document.getElementById('team-member-modal-backdrop');
        modalContent = modal?.querySelector('[data-modal-content]');

        return {
            modal,
            modalBackdrop,
            modalContent,
            closeButton: modal?.querySelector('[data-team-member-close]'),
            nameDisplay: modal?.querySelector('[data-team-member-name-display]'),
            roleDisplay: modal?.querySelector('[data-team-member-role-display]'),
            bioContainer: modal?.querySelector('[data-team-member-bio-container]'),
            bioText: modal?.querySelector('[data-team-member-bio-text]'),
            contactContainer: modal?.querySelector('[data-team-member-contact]'),
            emailLink: modal?.querySelector('[data-team-member-email-link]'),
            emailText: modal?.querySelector('[data-team-member-email-text]'),
            phoneLink: modal?.querySelector('[data-team-member-phone-link]'),
            phoneText: modal?.querySelector('[data-team-member-phone-text]'),
            imageSrc: modal?.querySelector('[data-team-member-image-src]'),
            imagePlaceholder: modal?.querySelector('[data-team-member-image-placeholder]')
        };
    }

    /**
     * Open modal and populate with team member data
     */
    function openModal(memberData) {
        // Create modal from template
        const elements = createModalFromTemplate();

        if (!elements.modal || !elements.modalBackdrop) return;

        // Populate name
        if (elements.nameDisplay && memberData.name) {
            elements.nameDisplay.textContent = memberData.name;
        }

        // Populate role
        if (elements.roleDisplay && memberData.role) {
            elements.roleDisplay.textContent = memberData.role;
        }

        // Populate bio
        if (elements.bioContainer && elements.bioText && memberData.bio) {
            elements.bioText.innerHTML = memberData.bio;
            elements.bioContainer.classList.remove('hidden');
        } else if (elements.bioContainer) {
            elements.bioContainer.classList.add('hidden');
        }

        // Track if we have any contact info
        let hasContactInfo = false;

        // Check if contact details should be visible (lightswitch control)
        const showContactDetails = memberData.visibleContactDetails === '1' || memberData.visibleContactDetails === true;

        // Populate email
        if (elements.emailLink && elements.emailText && memberData.email && showContactDetails) {
            elements.emailLink.href = `mailto:${memberData.email}`;
            elements.emailLink.title = `Click to email ${memberData.name}`;
            elements.emailText.textContent = memberData.email;
            elements.emailLink.classList.remove('hidden');
            hasContactInfo = true;
        } else if (elements.emailLink) {
            elements.emailLink.classList.add('hidden');
        }

        // Populate phone
        if (elements.phoneLink && elements.phoneText && memberData.phone && showContactDetails) {
            elements.phoneLink.href = `tel:${memberData.phone}`;
            elements.phoneLink.title = `Click to call ${memberData.name}`;
            elements.phoneText.textContent = memberData.phone;
            elements.phoneLink.classList.remove('hidden');
            hasContactInfo = true;
        } else if (elements.phoneLink) {
            elements.phoneLink.classList.add('hidden');
        }

        // Show/hide contact container based on whether we have any contact info
        if (elements.contactContainer) {
            if (hasContactInfo) {
                elements.contactContainer.classList.remove('hidden');
            } else {
                elements.contactContainer.classList.add('hidden');
            }
        }

        // Populate image
        if (elements.imageSrc && memberData.image) {
            elements.imageSrc.src = memberData.image;
            elements.imageSrc.alt = memberData.name || 'Team member';
            elements.imageSrc.classList.remove('hidden');
            if (elements.imagePlaceholder) {
                elements.imagePlaceholder.classList.add('hidden');
            }
        } else {
            if (elements.imageSrc) {
                elements.imageSrc.classList.add('hidden');
            }
            if (elements.imagePlaceholder) {
                elements.imagePlaceholder.classList.remove('hidden');
            }
        }

        // Set up close handlers
        if (elements.closeButton) {
            elements.closeButton.addEventListener('click', closeModal);
        }

        if (elements.modalBackdrop) {
            elements.modalBackdrop.addEventListener('click', (event) => {
                if (event.target === elements.modalBackdrop) {
                    closeModal();
                }
            });
        }

        // Show modal with animation
        // Trigger reflow for animation
        setTimeout(() => {
            elements.modal.dataset.open = 'true';
            elements.modalBackdrop.dataset.open = 'true';
        }, 50);

        // Lock body scroll
        PageUtils.bodyLockToggleOn();
    }

    /**
     * Close modal and remove from DOM
     */
    function closeModal() {
        if (!modal || !modalBackdrop) return;

        modal.dataset.open = 'false';
        modalBackdrop.dataset.open = 'false';

        // Unlock body scroll
        PageUtils.bodyLockToggleOff();

        // Remove from DOM after animation
        setTimeout(() => {
            if (modal && modal.parentNode) {
                modal.parentNode.removeChild(modal);
            }
            if (modalBackdrop && modalBackdrop.parentNode) {
                modalBackdrop.parentNode.removeChild(modalBackdrop);
            }

            // Clear references
            modal = null;
            modalBackdrop = null;
            modalContent = null;
        }, 300);
    }

    /**
     * Event delegation for team member card clicks
     */
    document.addEventListener('click', (event) => {
        const teamMemberCard = event.target.closest('[data-view-team-member]');

        if (teamMemberCard) {
            // Extract data from card attributes
            const memberData = {
                name: teamMemberCard.dataset.teamMemberName || '',
                role: teamMemberCard.dataset.teamMemberRole || '',
                email: teamMemberCard.dataset.teamMemberEmail || '',
                phone: teamMemberCard.dataset.teamMemberPhone || '',
                bio: teamMemberCard.dataset.teamMemberBio || '',
                image: teamMemberCard.dataset.teamMemberImage || '',
                visibleContactDetails: teamMemberCard.dataset.teamMemberVisibleContactDetails || '0'
            };

            openModal(memberData);
        }
    });

    /**
     * Close modal on Escape key
     */
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal && modal.dataset.open === 'true') {
            closeModal();
        }
    });
});

